'use client';

import { type FactiiiRequirement, type FactiiiTag } from '@shared/all/domains/factiiis';
import { convertToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import useDefaultFactiiis from '@shared/utils/hooks/useDefaultFactiiis';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import { Calendar, CameraIcon, MapPin, Trash } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { client } from '../../../utils/trpc';
import { getUploadSrc } from '../../../utils/uploads';
import Button from '../../common/Button';
import { Camera } from '../../common/Camera';
import DefaultDateTimeFactiii from '../../factiiis/FactiiiRenderers/DefaultDateTime';
import DefaultLocationFactiii from '../../factiiis/FactiiiRenderers/DefaultLocationFactiii';
import Modal, { ModalContent, ModalHeader } from '../../HOC/Modal';
import { TOOLBAR_ICON, toolbarButton } from '../toolbarButton';
type Props = {
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: React.Dispatch<React.SetStateAction<FactiiiTag[]>>;
  setNsfw: (nsfw: boolean) => void;
  setPolitics: (politics: boolean) => void;
  initialLocation?: {
    latitude: number;
    longitude: number;
  } | null;
  requirementsMet: {
    tag: FactiiiTag;
    results: {
      completed: boolean;
      description: string;
      requirement: FactiiiRequirement;
    }[];
  }[];
};
export const DefaultFactiiiButtons = (props: Props) => {
  const {
    addFactiii,
    dateChangeHandler: sharedDateChange,
    locationChangeHandler: sharedLocationChange
  } = useFactiiiHandlers();
  const [activeModal, setActiveModal] = useState<'camera' | 'date' | 'location' | null>(null);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const defaultFactiiiIds = useDefaultFactiiis(client);
  const {
    getFactiiiId
  } = useFactiiis(client);
  const defaultLocationId = defaultFactiiiIds.location;
  const defaultDateTimeId = defaultFactiiiIds.datetime;
  const defaultCameraId = defaultFactiiiIds.camera;
  const getDefaultTag = () => {
    const currentTimezone = {
      value: Intl.DateTimeFormat().resolvedOptions().timeZone,
      label: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: -new Date().getTimezoneOffset() / 60
    };
    return {
      id: 0,
      factiiiId: 0,
      name: '',
      slug: '',
      description: '',
      requirements: [],
      avatarKey: null,
      robohash: null,
      bannerKey: null,
      isNewFactiii: true,
      isRemovedFactiii: false,
      isPinnedFactiii: false,
      spaceSlug: '',
      isBot: false,
      isRetired: false,
      replyConfig: undefined,
      postFactiiis: [{
        data: '',
        number: null,
        spaceTimes: [{
          id: 0,
          timestamp: null,
          latitude: null,
          longitude: null,
          display: null,
          altitude: null,
          deviceVerifiedAt: null,
          date: '',
          // Empty so user can input custom date
          time: '',
          // Empty so user can input custom time
          timezone: currentTimezone,
          dateTimeError: false,
          locationValue: '',
          locationError: false
        }],
        uploads: [],
        references: [],
        factiiiReferences: [],
        anonymous: false
      }]
    } as FactiiiTag;
  };
  const closeModal = () => {
    setActiveModal(null);
    setShowPhotoPreview(false);
  };
  const getTagStatus = (tag?: FactiiiTag) => {
    if (!tag) {
      return 'inactive';
    }
    const requirementsMet = props.requirementsMet.find(t => t.tag.factiiiId === tag.factiiiId);
    if (!requirementsMet || tag.isRemovedFactiii) {
      return 'inactive';
    }
    return requirementsMet.results.every(result => result.completed) ? 'active' : 'errrored';
  };

  // The shared toolbar style, plus the tag's own state: active lights the
  // button, errored recolours the glyph.
  const statusButton = (factiiiId: number | undefined): string => {
    const status = getTagStatus(props.myPostFactiiiCounts.find(tag_0 => tag_0.factiiiId === factiiiId));
    return `${toolbarButton(status === 'active')} ${status === 'errrored' ? 'text-destructive' : ''}`;
  };
  useEffect(() => {
    const setInitialLocation = async () => {
      if (props.initialLocation) {
        const existingLocationTag = props.myPostFactiiiCounts.find(tag_1 => tag_1.factiiiId === defaultLocationId);
        if (existingLocationTag) {
          sharedLocationChange({
            tag: existingLocationTag,
            value: `${props.initialLocation.latitude},${props.initialLocation.longitude}`,
            myPostFactiiiCounts: props.myPostFactiiiCounts,
            setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
          });
        } else {
          // Add new location tag if it doesn't exist
          const factiii = await getFactiiiId(defaultLocationId ?? 0);
          if (factiii) {
            const locationTag = convertToFactiiiTag(factiii);
            addFactiii({
              tag: locationTag,
              setMyPostFactiiiCounts: props.setMyPostFactiiiCounts,
              setPolitics: props.setPolitics,
              setNsfw: props.setNsfw,
              myPostFactiiiCounts: props.myPostFactiiiCounts
            });
            sharedLocationChange({
              tag: locationTag,
              value: `${props.initialLocation.latitude},${props.initialLocation.longitude}`,
              myPostFactiiiCounts: props.myPostFactiiiCounts,
              setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
            });
          }
        }
      }
    };
    setInitialLocation();
  }, [props.initialLocation]);
  const triggerRemove = (tag_2: FactiiiTag) => {
    closeModal();
    props.setMyPostFactiiiCounts(prev => prev.map(t_0 => {
      if (t_0.factiiiId === tag_2.factiiiId) {
        if (t_0.isRemovedFactiii) return {
          ...t_0,
          isRemovedFactiii: false
        };else return {
          ...t_0,
          isRemovedFactiii: true
        };
      }
      return t_0;
    }));
  };
  const TrashButton = ({
    tag: tag_3
  }: {
    tag: FactiiiTag;
  }) => {
    return tag_3.isNewFactiii ? null : <Button variant="CLEAR" classNames="p-1.5 ml-auto" icon={<Trash width={20} height={20} color={tag_3.isRemovedFactiii ? 'red' : 'rgba(var(--text-secondary-color))'} />} tooltip={tag_3.isRemovedFactiii ? 'Marked for removal' : 'remove'} onClick={() => triggerRemove(tag_3)} />;
  };
  const LocationFactiii = (() => {
    const tag_5 = props.myPostFactiiiCounts.find(tag_4 => tag_4.factiiiId === defaultLocationId) || getDefaultTag();
    return <Modal closeHandler={closeModal}>
        <ModalHeader closeHandler={closeModal}>
          <h2 className="text-lg font-semibold text-textPrimary">Location</h2>
        </ModalHeader>
        <ModalContent>
          <DefaultLocationFactiii triggerRemove={() => triggerRemove(tag_5)} disabled={!tag_5.isNewFactiii} tag={tag_5} locationSaveHandler={async value => {
          const shouldClear = !value || value.trim() === '';
          let finalTag = tag_5;
          closeModal();
          if (shouldClear) {
            if (finalTag.isNewFactiii) {
              return props.setMyPostFactiiiCounts(prev_0 => prev_0.filter(t_1 => t_1.factiiiId !== tag_5.factiiiId));
            } else {
              finalTag.isRemovedFactiii = true;
            }
          } else if (!props.myPostFactiiiCounts.find(t_2 => t_2.factiiiId === tag_5.factiiiId)) {
            const factiii_0 = await getFactiiiId(defaultLocationId ?? 0);
            if (factiii_0) {
              finalTag = convertToFactiiiTag(factiii_0);
              finalTag.isNewFactiii = true;
              addFactiii({
                tag: finalTag,
                setMyPostFactiiiCounts: props.setMyPostFactiiiCounts,
                setPolitics: props.setPolitics,
                setNsfw: props.setNsfw,
                myPostFactiiiCounts: props.myPostFactiiiCounts
              });
            }
          }
          sharedLocationChange({
            tag: finalTag,
            value,
            myPostFactiiiCounts: props.myPostFactiiiCounts,
            setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
          });
        }} />
        </ModalContent>
      </Modal>;
  })();
  const DateTimeFactiii = (() => {
    const tag_7 = props.myPostFactiiiCounts.find(tag_6 => tag_6.factiiiId === defaultDateTimeId) || getDefaultTag();
    return <Modal closeHandler={closeModal}>
        <ModalHeader closeHandler={closeModal}>
          <h2 className="text-lg font-semibold text-textPrimary">
            Date & Time
          </h2>
        </ModalHeader>
        <ModalContent>
          <DefaultDateTimeFactiii triggerRemove={() => triggerRemove(tag_7)} disabled={!tag_7.isNewFactiii} tag={props.myPostFactiiiCounts.find(tag_8 => tag_8.factiiiId === defaultDateTimeId) || getDefaultTag()} dateSaveHandler={async (date, time, timezone) => {
          const shouldClear_0 = (!date || date.trim() === '') && (!time || time.trim() === '');
          closeModal();
          let finalTag_0 = tag_7;
          if (shouldClear_0) {
            if (finalTag_0.isNewFactiii) {
              return props.setMyPostFactiiiCounts(prev_1 => prev_1.filter(t_3 => t_3.factiiiId !== tag_7.factiiiId));
            } else {
              finalTag_0.isRemovedFactiii = true;
            }
          } else if (!props.myPostFactiiiCounts.find(t_4 => t_4.factiiiId === tag_7.factiiiId)) {
            const factiii_1 = await getFactiiiId(defaultDateTimeId ?? 0);
            if (factiii_1) {
              finalTag_0 = convertToFactiiiTag(factiii_1);
              finalTag_0.isNewFactiii = true;
              addFactiii({
                tag: finalTag_0,
                setMyPostFactiiiCounts: props.setMyPostFactiiiCounts,
                setPolitics: props.setPolitics,
                setNsfw: props.setNsfw,
                myPostFactiiiCounts: props.myPostFactiiiCounts
              });
            }
          }
          sharedDateChange({
            tag: finalTag_0,
            date,
            time,
            timezone,
            myPostFactiiiCounts: props.myPostFactiiiCounts,
            setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
          });
        }} defaultTimezone={{
          value: Intl.DateTimeFormat().resolvedOptions().timeZone,
          label: Intl.DateTimeFormat().resolvedOptions().timeZone,
          offset: -new Date().getTimezoneOffset() / 60
        }} />
        </ModalContent>
      </Modal>;
  })();
  const DefaultCameraFactiii = (() => {
    const tag_10 = props.myPostFactiiiCounts.find(tag_9 => tag_9.factiiiId === defaultCameraId) || getDefaultTag();
    return showPhotoPreview || !tag_10.isNewFactiii ? <Modal closeHandler={closeModal}>
        <ModalHeader closeHandler={closeModal}>
          <h2 className="text-lg font-semibold text-textPrimary">
            Photo Preview
          </h2>
          <TrashButton tag={tag_10} />
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div className="relative">
              <Image src={props.myPostFactiiiCounts.find(tag_12 => tag_12.factiiiId === defaultCameraId)?.postFactiiis[0]?.photo?.uri || getUploadSrc(props.myPostFactiiiCounts.find(tag_11 => tag_11.factiiiId === defaultCameraId)?.postFactiiis[0]?.uploads?.[0])} alt="Captured photo" width={400} height={300} className="w-full rounded object-cover" />
            </div>
            <div className="flex justify-between">
              <Button disabled={!tag_10.isNewFactiii} text="Remove Photo" variant="SECONDARY" onClick={() => {
              const cameraTag = props.myPostFactiiiCounts.find(tag_13 => tag_13.factiiiId === defaultCameraId);
              if (cameraTag) {
                if (cameraTag.isNewFactiii) {
                  props.setMyPostFactiiiCounts(props.myPostFactiiiCounts.filter(t_5 => t_5.id !== cameraTag.id));
                } else {
                  props.setMyPostFactiiiCounts(props.myPostFactiiiCounts.map(t_6 => t_6.id === cameraTag.id ? {
                    ...t_6,
                    isRemovedFactiii: true
                  } : t_6));
                }
              }
              setShowPhotoPreview(false);
            }} classNames="bg-destructive hover:bg-destructive/90 text-white" />
              <Button text="Done" onClick={closeModal} />
            </div>
          </div>
        </ModalContent>
      </Modal> : <Camera closeHandler={closeModal} setNewPhotoFcn={async photo => {
      if (!defaultCameraId) return;
      const factiii_2 = await getFactiiiId(defaultCameraId);
      if (!factiii_2) return;
      const cameraTag_0 = convertToFactiiiTag(factiii_2);
      cameraTag_0.postFactiiis[0].photo = photo;
      addFactiii({
        tag: cameraTag_0,
        setMyPostFactiiiCounts: props.setMyPostFactiiiCounts,
        setPolitics: props.setPolitics,
        setNsfw: props.setNsfw,
        myPostFactiiiCounts: props.myPostFactiiiCounts
      });
      closeModal();
    }} />;
  })();
  return <>
      <div className="flex items-center gap-0.5">
        {defaultDateTimeId && <button title="Date & Time" className={statusButton(defaultDateTimeId)} onClick={() => {
        setActiveModal('date');
      }}>
            <Calendar size={TOOLBAR_ICON} />
          </button>}
        {defaultLocationId && <button title="Location" className={statusButton(defaultLocationId)} onClick={() => {
        setActiveModal('location');
      }}>
            <MapPin size={TOOLBAR_ICON} />
          </button>}
        {defaultCameraId && <button title="Take Photo" className={statusButton(defaultCameraId)} onClick={() => {
        const cameraTag_1 = props.myPostFactiiiCounts.find(tag_14 => tag_14.factiiiId === defaultCameraId);
        const hasPhoto = cameraTag_1?.postFactiiis[0]?.photo || (cameraTag_1?.postFactiiis[0]?.uploads?.length ?? 0) > 0;
        if (hasPhoto) {
          setActiveModal('camera');
          setShowPhotoPreview(true);
        } else {
          setActiveModal('camera');
          setShowPhotoPreview(false);
        }
      }}>
            <CameraIcon size={TOOLBAR_ICON} />
          </button>}
      </div>

      {/* Camera Modal */}
      {activeModal === 'camera' && <>{DefaultCameraFactiii}</>}

      {/* Date & Time Modal */}
      {activeModal === 'date' && <>{DateTimeFactiii}</>}

      {/* Location Modal */}
      {activeModal === 'location' && <>{LocationFactiii}</>}
    </>;
};